import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SkyButton } from "@skypointcloud/skypoint-ui";
import { X as DismissIcon } from "lucide-react";
import { watchForNewVersion } from "../../../utils/newVersion";

/** The CSS variable everything offset for the fixed header adds (index.css). */
const HEIGHT_VAR = "--new-version-h";

/**
 * "A new version is available", once a newer version is deployed while this
 * tab is open (see utils/newVersion): a strip above the header that moves
 * the page down while it shows. Never reloads by
 * itself: anything typed but not saved stays until the person refreshes.
 */
export function NewVersionNotice() {
  const [available, setAvailable] = useState(false);
  const strip = useRef<HTMLDivElement>(null);

  useEffect(() => watchForNewVersion(() => setAvailable(true)), []);

  // The page below moves down by the strip's height, whatever it wraps to.
  useLayoutEffect(() => {
    const el = strip.current;
    const root = document.documentElement;
    if (!available || !el) return;
    const measure = () => root.style.setProperty(HEIGHT_VAR, `${el.offsetHeight}px`);
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, [available]);

  if (!available) return null;

  return (
    <div
      ref={strip}
      data-testid="new-version-notice"
      role="status"
      className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-amber-200 bg-amber-50 px-4 py-1.5 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100"
    >
      <span>A new version is available.</span>
      <SkyButton
        appearance="primary"
        size="small"
        onClick={() => window.location.reload()}
      >
        Refresh
      </SkyButton>
      <SkyButton
        appearance="transparent"
        size="small"
        icon={DismissIcon}
        aria-label="Dismiss"
        onClick={() => setAvailable(false)}
      />
    </div>
  );
}
